import {
  sendQuery,
  recipientsQuery,
  recipientPreviewQuery,
  useCancelSendMutation,
  useRetryRecipientMutation,
  type EmailSendRecipient,
} from "@/api/campaigns";
import { StyledLink } from "@/components/link/StyledLink";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableFooter } from "@/components/data-table/DataTableFooter";
import { DataTableHeader } from "@/components/data-table/DataTableHeader";
import { Button } from "@/components/ui/Button";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { Tooltip } from "@/components/ui/Tooltip";
import { seo } from "@/utils/seo";
import { Box, Flex, Text, toast } from "@givenwell/components";
import { colors } from "@givenwell/theme";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { fallback, number, object, optional, pipe, toMinValue } from "valibot";

export const Route = createFileRoute("/campaigns/sends/$sendId")({
  component: SendDetail,
  validateSearch: object({
    page: fallback(pipe(optional(number(), 1), toMinValue(1)), 1),
    "page-size": fallback(pipe(optional(number(), 25), toMinValue(1)), 25),
  }),
  head: () => ({ meta: seo({ title: "Send Detail" }) }),
});

const STATUS_COLORS: Record<string, string> = {
  draft: colors.text2,
  scheduled: "#6366f1",
  evaluating: "#6366f1",
  sending: "#f59e0b",
  completed: "#22c55e",
  failed: "#ef4444",
  cancelled: colors.text2,
  pending: colors.text2,
  sent: "#22c55e",
  skipped: "#f59e0b",
};

function StatusBadge({ status }: { status: string }) {
  return (
    <Box
      css={{
        display: "inline-block",
        px: 8,
        py: 2,
        rounded: 999,
        fontScale: "xs",
        fontWeight: 500,
        color: STATUS_COLORS[status] || colors.text2,
        border: `1px solid ${STATUS_COLORS[status] || colors.outline}`,
      }}
    >
      {status}
    </Box>
  );
}

function SendDetail() {
  const { sendId } = Route.useParams();
  const search = Route.useSearch();
  const { page, "page-size": pageSize } = search;
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const sendQ = useQuery(sendQuery(sendId));
  const recipientsQ = useQuery(recipientsQuery(sendId, page, pageSize));
  const cancelMut = useCancelSendMutation();
  const retryMut = useRetryRecipientMutation(sendId);

  async function handlePreview(recipientId: string) {
    const data = await queryClient.fetchQuery(recipientPreviewQuery(sendId, recipientId));
    window.open(data.url, "_blank");
  }

  const send = sendQ.data;
  const recipients = recipientsQ.data?.items || [];

  if (!send) {
    return (
      <PageWrapper title="Send Detail">
        <Text>Loading...</Text>
      </PageWrapper>
    );
  }

  const canCancel = send.status === "draft" || send.status === "scheduled" || send.status === "evaluating";

  return (
    <PageWrapper
      title={send.name}
      rightContent={
        <Flex css={{ gap: 8 }}>
          <Button variant="plain" color="neutral" onClick={() => navigate({ to: "/campaigns/sends" })}>
            Back
          </Button>
          {canCancel && (
            <Button
              variant="outlined"
              color="red"
              onClick={() => cancelMut.mutate(sendId, { onSuccess: () => toast.success("Cancelled") })}
              loading={cancelMut.isPending}
            >
              Cancel Send
            </Button>
          )}
        </Flex>
      }
    >
      {/* Send summary */}
      <Box css={{ background: "white", p: 16, rounded: 12, mb: 16 }}>
        <Flex css={{ gap: 24, flexWrap: "wrap" }}>
          <Box>
            <Text css={{ fontScale: "xs", color: colors.text2 }}>Status</Text>
            <StatusBadge status={send.status} />
          </Box>
          <Box>
            <Text css={{ fontScale: "xs", color: colors.text2 }}>Recipients</Text>
            <Text css={{ fontWeight: 600 }}>{send.total_recipients}</Text>
          </Box>
          <Box>
            <Text css={{ fontScale: "xs", color: colors.text2 }}>Sent</Text>
            <Text css={{ fontWeight: 600, color: "#22c55e" }}>{send.sent_count}</Text>
          </Box>
          <Box>
            <Text css={{ fontScale: "xs", color: colors.text2 }}>Failed</Text>
            <Text css={{ fontWeight: 600, color: (send.failed_count ?? 0) > 0 ? "#ef4444" : undefined }}>
              {send.failed_count}
            </Text>
          </Box>
          <Box>
            <Tooltip content="Members who were already unsubscribed and did not receive this send.">
              <span style={{ cursor: "default" }}>
                <Text css={{ fontScale: "xs", color: colors.text2 }}>Skipped</Text>
              </span>
            </Tooltip>
            <Text css={{ fontWeight: 600, color: colors.text2 }}>{send.skipped_count ?? 0}</Text>
          </Box>
          <Box>
            <Tooltip content="Members who unsubscribed after receiving this send.">
              <span style={{ cursor: "default" }}>
                <Text css={{ fontScale: "xs", color: colors.text2 }}>Unsubscribed</Text>
              </span>
            </Tooltip>
            <Text css={{ fontWeight: 600, color: colors.text2 }}>{send.unsubscribed_count ?? 0}</Text>
          </Box>
          <Box>
            <Tooltip
              content={
                send.track_opens === false
                  ? "Open tracking is disabled for this send."
                  : "Members who opened this email (tracking pixel loaded)."
              }
            >
              <span style={{ cursor: "default" }}>
                <Text css={{ fontScale: "xs", color: colors.text2 }}>Opened</Text>
              </span>
            </Tooltip>
            <Text css={{ fontWeight: 600, color: colors.text2 }}>
              {send.track_opens === false ? "N/A" : (send.opened_count ?? 0)}
            </Text>
          </Box>
          {send.scheduled_at && (
            <Box>
              <Text css={{ fontScale: "xs", color: colors.text2 }}>Scheduled</Text>
              <Text css={{ fontWeight: 500 }}>{new Date(send.scheduled_at).toLocaleString()}</Text>
            </Box>
          )}
          {send.started_at && (
            <Box>
              <Text css={{ fontScale: "xs", color: colors.text2 }}>Started</Text>
              <Text css={{ fontWeight: 500 }}>{new Date(send.started_at).toLocaleString()}</Text>
            </Box>
          )}
          {send.completed_at && (
            <Box>
              <Text css={{ fontScale: "xs", color: colors.text2 }}>Completed</Text>
              <Text css={{ fontWeight: 500 }}>{new Date(send.completed_at).toLocaleString()}</Text>
            </Box>
          )}
        </Flex>
      </Box>

      {/* Recipients table */}
      <DataTable>
        <DataTableHeader hideToggle />
        <DataTableBody
          data={recipients}
          status={recipientsQ.status}
          dataKey="email_send_recipient_id"
          columns={[
            {
              header: "Email",
              cell: (r: EmailSendRecipient) => (
                <StyledLink to="/admin/members/$memberId" params={{ memberId: r.member_id }}>
                  {r.email}
                </StyledLink>
              ),
            },
            {
              header: "Status",
              cell: (r: EmailSendRecipient) => <StatusBadge status={r.status} />,
            },
            {
              header: "Sent At",
              cell: (r: EmailSendRecipient) => (r.sent_at ? new Date(r.sent_at).toLocaleString() : "-"),
            },
            {
              header: "Opened At",
              cell: (r: EmailSendRecipient) =>
                send.track_opens === false ? "N/A" : r.opened_at ? new Date(r.opened_at).toLocaleString() : "-",
            },
            {
              header: "Unsubscribed",
              cell: (r: EmailSendRecipient) =>
                r.unsubscribed ? <Text css={{ fontScale: "xs", color: colors.text2 }}>Yes</Text> : "-",
            },
            {
              header: "Error",
              cell: (r: EmailSendRecipient) =>
                r.error_message ? (
                  <Flex css={{ alignItems: "center", gap: "6px" }}>
                    <Text css={{ fontScale: "xs", color: "#ef4444" }}>
                      {r.error_message.length > 80 ? r.error_message.slice(0, 80) + "…" : r.error_message}
                    </Text>
                    <Button
                      variant="plain"
                      size="xs"
                      onClick={() => {
                        navigator.clipboard.writeText(r.error_message!);
                        toast.success("Copied to clipboard");
                      }}
                    >
                      Copy
                    </Button>
                  </Flex>
                ) : (
                  "-"
                ),
            },
            {
              header: "",
              cell: (r: EmailSendRecipient) => (
                <Flex css={{ gap: 8, justifyContent: "flex-end" }}>
                  {r.status === "failed" && (
                    <Button
                      variant="tonal"
                      size="xs"
                      disabled={retryMut.isPending}
                      onClick={() =>
                        retryMut.mutate(r.email_send_recipient_id, {
                          onSuccess: () => toast.success("Retry queued"),
                          onError: () => toast.error("Failed to retry"),
                        })
                      }
                    >
                      Retry
                    </Button>
                  )}
                  {r.s3_key ? (
                    <Button variant="tonal" size="xs" onClick={() => handlePreview(r.email_send_recipient_id)}>
                      View email
                    </Button>
                  ) : null}
                </Flex>
              ),
            },
          ]}
        />
        <DataTableFooter itemCount={recipientsQ.data?.total_items} />
      </DataTable>
    </PageWrapper>
  );
}
